import {
  DriverAccommodationType,
  RideOfferType,
  encodeRideLegs,
  licensePlateSchemaObject,
  locationBaseSchema,
  multiStopLegsSchema,
  offerTripDtoSchema,
  operatorOfferDtoSchema,
  passengerCountWithMaxSchemaObject,
  startAndEndLocationObjectSchema,
  startAndReturnTimeObjectSchema,
  startTimeObjectSchema,
  zo,
} from 'remote/global';
import { CreateRideOfferEnum } from 'utils/data/enums';

const changeRideEndSchema = zo.object({
  rideId: zo.number(), // only used internally
  newFinishTime: zo.string().datetime(),
});

const supplierSchema = zo.object({
  clientId: zo.number().optional().or(zo.literal('')),
});

const commonRideSchema = passengerCountWithMaxSchemaObject.and(supplierSchema).and(
  zo.object({
    licensePlate: licensePlateSchemaObject.or(zo.literal('')),
    driverLanguage: zo.string().min(1).max(2).optional().or(zo.undefined()),
  })
);

const oneWayRideSchema = startAndEndLocationObjectSchema
  .and(startTimeObjectSchema)
  .and(commonRideSchema)
  .and(zo.object({ type: zo.nativeEnum(RideOfferType) }));

const roundTripRideSchema = startAndEndLocationObjectSchema
  .and(startAndReturnTimeObjectSchema)
  .and(commonRideSchema)
  .and(zo.object({ type: zo.nativeEnum(RideOfferType) }));

const multiStopRideSchema = commonRideSchema.and(
  zo
    .object({
      type: zo.nativeEnum(RideOfferType),
      legs: multiStopLegsSchema,
    })
    .transform((arg) => ({ ...arg, legs: encodeRideLegs(arg.legs) })) // transform legs to the format BE can understand))
);

// TODO: Endlocation is optional for city hire
const cityHireRideSchema = commonRideSchema
  .and(
    zo
      .object({
        startLocation: locationBaseSchema,
        endLocation: locationBaseSchema.optional(),
      })
      .transform((arg) => ({ ...arg, endLocation: arg.startLocation })) // assigns endLocation as the same as startLocation)
  )
  .and(startTimeObjectSchema)
  .and(
    zo.object({
      duration: zo.coerce.string().min(1).pipe(zo.coerce.number().int()),
      type: zo.nativeEnum(RideOfferType),
    })
  );

const rideDetailsSchema = zo.union([
  oneWayRideSchema,
  roundTripRideSchema,
  multiStopRideSchema,
  cityHireRideSchema,
]);

const driverAccommodationSchemaObject = zo.nativeEnum(DriverAccommodationType).optional();
const rideExtrasSchemaObject = zo.array(zo.any());

const rideOffersPayloadSchema = zo.intersection(
  rideDetailsSchema,
  zo.object({
    driverAccommodationType: driverAccommodationSchemaObject,
    extras: rideExtrasSchemaObject.optional(),
    onlySupplierOffers: zo.boolean().optional(),
    operatorId: zo.number().optional(),
    validUntil: zo.string().datetime().optional(),
  })
);

const selectedOffersSchema = zo.object({
  trips: zo.array(offerTripDtoSchema),
  defaultOperatorOffers: zo.array(operatorOfferDtoSchema), // this is used internally to determine changed keys against the base object
  operatorOfferUuids: zo.array(zo.string()).min(1), // this is only used internally to store the selected offer uuids, validation is done after
  selectedOperatorOfferUuid: zo.string().optional(), // only required when CREATE_OFFER, validation is done afterwards
});

const rideOffersSchema = selectedOffersSchema.merge(
  zo.object({
    type: zo.nativeEnum(RideOfferType),
    refNumber: zo.string(),
    extras: rideExtrasSchemaObject,
    passengerCount: zo.number(),
  })
);

const rideSupplierSchema = supplierSchema
  .merge(rideOffersSchema)
  .merge(
    zo.object({
      rideOfferType: zo.nativeEnum(CreateRideOfferEnum), // this will be used to determine if an order should be created or and offer should be sent
      clientContactEmail: zo.string().email().or(zo.literal('')),
      returnScheduleItem: zo.boolean(),
      lang: zo.string(),
      validUntil: zo.string().datetime().optional().or(zo.literal('')),
    })
  )
  .superRefine(
    (
      { rideOfferType, validUntil, clientContactEmail, clientId, selectedOperatorOfferUuid },
      ctx
    ) => {
      if (rideOfferType === CreateRideOfferEnum.CREATE_ORDER) {
        // clientId is required when creating an order
        if (clientId == null || clientId === '') {
          ctx.addIssue({
            code: zo.ZodIssueCode.custom,
            params: { i18n: 'required' },
            path: ['clientId'],
          });
        }

        // selectedOperatorOfferUuid is required when creating an order
        if (selectedOperatorOfferUuid == null || selectedOperatorOfferUuid.length <= 0) {
          ctx.addIssue({
            code: zo.ZodIssueCode.custom,
            params: { i18n: 'required' },
            path: ['selectedOperatorOfferUuid'],
          });
        }
      }

      if (rideOfferType === CreateRideOfferEnum.SEND_OFFER) {
        // Contact email is required when sending the offer
        if (clientContactEmail == null || clientContactEmail.length <= 0) {
          ctx.addIssue({
            code: zo.ZodIssueCode.custom,
            params: { i18n: 'required' },
            path: ['clientContactEmail'],
          });
        }

        if (validUntil == null || validUntil.length <= 0) {
          ctx.addIssue({
            code: zo.ZodIssueCode.custom,
            params: { i18n: 'required' },
            path: ['validUntil'],
          });
        }
      }
    }
  );

type OneWayRideSchemaType = zo.infer<typeof oneWayRideSchema>;
type RoundTripRideSchemaType = zo.infer<typeof roundTripRideSchema>;
type MultiStopRideSchemaType = zo.infer<typeof multiStopRideSchema>;
type CityHireRideSchemaType = zo.infer<typeof cityHireRideSchema>;
type ChangeRideEndSchemaType = zo.infer<typeof changeRideEndSchema>;
type RideDetailsSchemaType = zo.infer<typeof rideDetailsSchema>;
type RideOffersPayloadSchemaType = zo.infer<typeof rideOffersPayloadSchema>;
type RideOffersSchemaType = zo.infer<typeof rideOffersSchema>;
type RideSupplierSchemaType = zo.infer<typeof rideSupplierSchema>;

export {
  changeRideEndSchema,
  cityHireRideSchema,
  driverAccommodationSchemaObject,
  multiStopLegsSchema,
  multiStopRideSchema,
  oneWayRideSchema,
  rideDetailsSchema,
  rideExtrasSchemaObject,
  rideOffersPayloadSchema,
  rideOffersSchema,
  rideSupplierSchema,
  roundTripRideSchema,
};
export type {
  ChangeRideEndSchemaType,
  CityHireRideSchemaType,
  MultiStopRideSchemaType,
  OneWayRideSchemaType,
  RideDetailsSchemaType,
  RideOffersPayloadSchemaType,
  RideOffersSchemaType,
  RideSupplierSchemaType,
  RoundTripRideSchemaType,
};
